import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ReactComponent as InformIcon } from '../../assets/images/icons/warning_icon.svg';
import './request.scss';
import { getUserDetails } from '../../API/Services/authService';
import ReviewRequest from './ReviewRequest';
import { useAlert } from 'react-alert';
import {
  initiateSalaryAdvance,
  initiateSalaryTopup,
} from '../../API/Services/requestService';
import LoaderWrapper from '../../Components/LoaderWrapper/LoaderWrapper';
import ErrorModal from '../../Components/ErrorModal/ErrorModal';

const DURATION_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12

const RequestMoney = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    resetField,
  } = useForm();

  const [reviewRequest, setReviewRequest] = useState(false);
  const [loader, setLoader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState({});
  const [user, setUser] = useState([]);

  const [processFeeDetails, setProcessFeeDetails] = useState(null);
  const [loanType, setLoanType] = useState('salaryAdvance'); // 'salaryAdvance' | 'salaryTopUp'
  const duration = watch('duration');

  const [processingFeePreview, setProcessingFeePreview] = useState(0);
  const alert = useAlert();

  useEffect(() => {
    setLoader(true);
    getUserDetails()
      .then((response) => {
        setLoader(false);
        if (response?.data?.status === 'OK') {
          setUser(response?.data?.data?.user);
        }
      })
      .catch(() => setLoader(false));
  }, []);

  const handleToggleError = () => setIsOpen(!isOpen);

  const handleInterestPreview = (e) => {
    const amount = Number(e.target.value || 0);
    if (amount <= 0) return setProcessingFeePreview(0);
    if (amount <= 25000) return setProcessingFeePreview(500);
    if (amount <= 50000) return setProcessingFeePreview(1000);
    if (amount <= 75000) return setProcessingFeePreview(1500);
    if (amount <= 100000) return setProcessingFeePreview(2000);
    return setProcessingFeePreview(2500);
  };

  const toggleReviewRequest = () => setReviewRequest((s) => !s);

  const handleRequest = ({ amount, duration }) => {
    if (user?.isSendForKYCApproval !== 4) {
      setMessage('Your profile is not completed, please contact admin.');
      return handleToggleError();
    }
    setLoader(true);
    const amt = Number(amount);

    const call =
      loanType === 'salaryTopUp'
        ? initiateSalaryTopup(amt, Number(duration))
        : initiateSalaryAdvance(amt);

    call
      .then((res) => {
        setLoader(false);
        if (res?.data?.status === 'OK') {
          alert.success(res?.data?.message);
          const req = res?.data?.data?.request || res?.data?.data?.processFeeInfo || {};
          const isTopUp = loanType === 'salaryTopUp';

          const requested = Number(req?.amount ?? req?.requestedAmount ?? req?.amountRequested ?? 0);

          const totalRepay = Number(req?.totalAmountToBePaid ?? 0);
          const feeExplicit = req?.serviceCharge ?? req?.interestAmount;
          const interest = feeExplicit != null ? Number(feeExplicit) : isTopUp && totalRepay ? totalRepay - requested : 0;

          const salaryDueDate = req?.paymentDate ?? req?.salaryDueDate ?? req?.endDate;

          setProcessFeeDetails({
            requestedAmount: requested,
            interestAmount: Number(interest),
            salaryDueDate,
            loanType,
            duration: isTopUp ? Number(duration) : undefined,
            totalAmountToBePaid: isTopUp ? totalRepay : requested + Number(interest),
            paymentBreakdown: isTopUp ? req?.paymentBreakdown || [] : undefined,
          });
          setReviewRequest(true);
        } else {
          setMessage(res?.data?.message || 'Something went wrong');
          handleToggleError();
        }
      })
      .catch((err) => {
        setLoader(false);
        setMessage(err?.message || 'Network error');
        handleToggleError();
      });
  };

  useEffect(() => {
    if (loanType === 'salaryAdvance') resetField('duration');
  }, [loanType, resetField]);

  if (loader) return <LoaderWrapper />;

  if (reviewRequest) {
    return (
      <ReviewRequest
        toggleReviewRequest={toggleReviewRequest}
        {...processFeeDetails}
      />
    );
  }

  return (
    <div className="request-page-container">
      <div className="request-card">
        <h2 className="request-heading">How much do you want?</h2>
        <p className="request-subheading">Select loan type and enter an amount to proceed.</p>

        <form className="form-wrapper" onSubmit={handleSubmit(handleRequest)}>
          <div className="loan-type-toggle">
            <label>
              <input
                type="radio"
                name="loanType"
                value="salaryAdvance"
                checked={loanType === 'salaryAdvance'}
                onChange={() => setLoanType('salaryAdvance')}
              />
              <span>Salary Advance</span>
            </label>
            <label>
              <input
                type="radio"
                name="loanType"
                value="salaryTopUp"
                checked={loanType === 'salaryTopUp'}
                onChange={() => setLoanType('salaryTopUp')}
              />
              <span>Salary Top-Up</span>
            </label>
          </div>

          <div className="input-group">
            <label htmlFor="amount" className="form-label">Amount</label>
            <div className="input-currency-wrapper">
               <span className="currency-symbol">₦</span>
               <input
                type="number"
                className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                {...register('amount', { required: 'Amount is required', min: { value: 1, message: 'Amount must be greater than 0'} })}
                id="amount"
                placeholder="50,000"
                onChange={handleInterestPreview}
              />
            </div>
            {errors.amount && <small className="error-msg">{errors.amount.message}</small>}
          </div>

          {loanType === 'salaryTopUp' && (
  <div className="input-group">
    <label htmlFor="duration" className="form-label">Duration (Months)</label>

    {/* wrapper gives us room for a custom arrow and fixes width */}
    <div className="select-wrapper">
      <select
        id="duration"
        className={`form-control select-control ${errors.duration ? 'is-invalid' : ''}`}
        defaultValue=""
        {...register('duration', {
          required: loanType === 'salaryTopUp' ? 'Please select a duration' : false,
        })}
      >
        <option value="" disabled>Select duration…</option>
        {DURATION_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m} {m === 1 ? 'month' : 'months'}
          </option>
        ))}
      </select>
    </div>

    {errors.duration && <small className="error-msg">{errors.duration.message}</small>}
  </div>
)}


          <div className="info-message">
            <InformIcon className="info-icon" />
            <p>
              Processing fee will be charged.
            </p>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary w-100">Proceed</button>
          </div>
        </form>
      </div>
      <ErrorModal isOpen={isOpen} toggleError={handleToggleError} message={message} />
    </div>
  );
};

export default RequestMoney;